<template>
    <div>
        <v-card outlined class="pa-4 mb-4">
            <div class="secao-titulo">Como usar o controle financeiro</div>
            <div class="caption grey--text text--darken-1">
                O que cada aba faz, de onde vem cada número e o que evitar. Comece pelo passo a passo.
            </div>
        </v-card>

        <v-card outlined class="pa-4 mb-4">
            <div class="bloco-titulo mb-3">Passo a passo do mês</div>

            <div v-for="(passo, indice) in passos" :key="passo.titulo" class="passo">
                <div class="passo__numero">{{ indice + 1 }}</div>
                <div class="passo__conteudo">
                    <div class="passo__titulo">{{ passo.titulo }}</div>
                    <div class="passo__texto">{{ passo.texto }}</div>
                </div>
            </div>

            <v-alert dense text type="info" class="caption mt-4 mb-0">
                Os passos 1 e 2 só precisam ser feitos uma vez. No dia a dia você repete o 3 e o 4.
            </v-alert>
        </v-card>

        <v-card outlined class="pa-4 mb-4">
            <div class="bloco-titulo mb-1">Como o lucro é calculado</div>
            <div class="caption grey--text text--darken-1 mb-3">
                Na ordem em que as coisas são descontadas. Cada linha parte do resultado da anterior.
            </div>

            <div class="cascata">
                <div v-for="etapa in cascata" :key="etapa.rotulo" class="cascata__linha" :class="etapa.classe">
                    <span class="cascata__operador">{{ etapa.operador }}</span>
                    <span class="cascata__rotulo">{{ etapa.rotulo }}</span>
                    <span class="cascata__nota">{{ etapa.nota }}</span>
                </div>
            </div>

            <v-alert dense text type="warning" class="caption mt-4 mb-0">
                Só entra no cálculo o que está com status <strong>Recebido</strong> (receitas) e
                <strong>Pago</strong> (despesas). Pendente, cancelado e despesa em aberto ficam de fora
                do resultado do mês, de propósito — aparecem à parte para você não esquecer deles.
            </v-alert>
        </v-card>

        <v-expansion-panels accordion multiple class="mb-4">
            <v-expansion-panel v-for="secao in secoes" :key="secao.titulo">
                <v-expansion-panel-header>
                    <div class="d-flex align-center">
                        <v-icon left :color="secao.cor">{{ secao.icone }}</v-icon>
                        <span class="font-weight-bold">{{ secao.titulo }}</span>
                    </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                    <p class="body-2 grey--text text--darken-2">{{ secao.resumo }}</p>

                    <div v-for="item in secao.itens" :key="item.nome" class="item-ajuda">
                        <div class="item-ajuda__nome">{{ item.nome }}</div>
                        <div class="item-ajuda__texto">{{ item.texto }}</div>
                    </div>

                    <v-alert v-if="secao.atencao" dense text type="warning" class="caption mt-3 mb-0">
                        {{ secao.atencao }}
                    </v-alert>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <v-card outlined class="pa-4 mb-4">
            <div class="bloco-titulo mb-3">Perguntas frequentes</div>

            <div v-for="duvida in duvidas" :key="duvida.pergunta" class="duvida">
                <div class="duvida__pergunta">{{ duvida.pergunta }}</div>
                <div class="duvida__resposta">{{ duvida.resposta }}</div>
            </div>
        </v-card>

        <v-card outlined class="pa-4">
            <div class="bloco-titulo mb-2">Quem tem acesso</div>
            <p class="body-2 mb-0">
                O controle financeiro é exclusivo do <strong>nível 5</strong> (administrador). Barbeiros
                e demais níveis não veem o item no menu, não conseguem abrir o endereço direto e são
                bloqueados também no banco de dados — não é só o botão que some.
            </p>
        </v-card>
    </div>
</template>

<script>
export default {
    data(){
        return {
            passos: [
                {
                    titulo: 'Confira as taxas em Configurações',
                    texto: 'Informe a taxa do débito e do crédito cobradas pela maquininha. '
                        + 'Elas são descontadas automaticamente de cada atendimento pago no cartão. '
                        + 'Se deixar zero, nenhuma taxa é descontada.'
                },
                {
                    titulo: 'Defina as comissões em Equipe',
                    texto: 'Para cada barbeiro, informe o percentual de corte, barba, corte + barba e outros, '
                        + 'além da meta mensal. É isso que determina quanto sai de comissão em cada atendimento.'
                },
                {
                    titulo: 'Importe os atendimentos em Receitas',
                    texto: 'O botão "Importar da agenda" traz os atendimentos do mês. Depois complete a forma '
                        + 'de pagamento e o status de cada linha e clique em Salvar. Pode importar quantas vezes '
                        + 'quiser: o que já veio não duplica.'
                },
                {
                    titulo: 'Lance as despesas do mês',
                    texto: 'Cadastre aluguel, produtos, contador e o que mais saiu do caixa, classificando cada uma. '
                        + 'Só as marcadas como Pago entram no lucro.'
                },
                {
                    titulo: 'Feche o mês pelo Painel',
                    texto: 'Confira o bloco "Conferência do fechamento" — ele aponta lançamento sem data, sem '
                        + 'pagamento ou sem valor. Com tudo OK, o lucro do mês está confiável.'
                }
            ],

            cascata: [
                { operador: '', rotulo: 'Faturamento bruto', nota: 'soma dos atendimentos recebidos', classe: 'cascata__linha--base' },
                { operador: '−', rotulo: 'Descontos', nota: 'o que você abateu do cliente' },
                { operador: '−', rotulo: 'Taxas de cartão', nota: 'só em débito e crédito' },
                { operador: '−', rotulo: 'Comissões', nota: 'percentual do barbeiro por categoria' },
                { operador: '=', rotulo: 'Receita após comissões', nota: 'o que sobra do atendimento', classe: 'cascata__linha--parcial' },
                { operador: '−', rotulo: 'Despesas operacionais', nota: 'custo de manter a barbearia; taxa de cartão fica acima' },
                { operador: '=', rotulo: 'Lucro do mês', nota: 'o que sobrou de fato', classe: 'cascata__linha--total' },
                { operador: '÷', rotulo: 'Margem líquida', nota: 'lucro dividido pelo faturamento menos descontos', classe: 'cascata__linha--parcial' }
            ],

            secoes: [
                {
                    titulo: 'Painel',
                    icone: 'mdi-view-dashboard',
                    cor: 'teal darken-2',
                    resumo: 'A fotografia de um mês. Escolha o mês no topo da tela e tudo se ajusta.',
                    itens: [
                        { nome: 'Os oito indicadores', texto: 'Seguem a ordem do cálculo do lucro, do faturamento bruto até a margem. A margem traz a meta ao lado, para comparação.' },
                        { nome: 'Despesas por classificação', texto: 'Divide o que foi pago entre fixas, variáveis, diretas e indiretas, com o percentual de cada grupo. Mostra onde o dinheiro está indo.' },
                        { nome: 'Conferência do fechamento', texto: 'Seis checagens de lançamento incompleto. Enquanto houver item "a revisar", os números podem estar subestimados.' },
                        { nome: 'Resultado por barbeiro', texto: 'Faturamento, comissão, líquido gerado e quanto da meta cada um atingiu. Lançamentos sem barbeiro aparecem em uma linha própria, para a soma fechar com o total.' },
                        { nome: 'Receita por categoria', texto: 'Quanto cada tipo de serviço rendeu no mês.' }
                    ]
                },
                {
                    titulo: 'Consolidado',
                    icone: 'mdi-chart-timeline-variant',
                    cor: 'teal darken-2',
                    resumo: 'A visão de vários meses, para enxergar tendência em vez de um mês isolado.',
                    itens: [
                        { nome: 'Período', texto: 'Escolha as datas ou use os atalhos de 3, 6, 12 e 24 meses, que sempre terminam no mês atual e contam para trás.' },
                        { nome: 'Gráfico mês a mês', texto: 'Faturamento, despesas e lucro na mesma escala. Passe o mouse em um mês para ver os valores. Mês com prejuízo aparece com a barra abaixo da linha do zero.' },
                        { nome: 'Por que abre rápido', texto: 'Esta aba lê um resumo pronto de cada mês, em vez de varrer todos os lançamentos. O resumo é regravado sozinho sempre que algum número do mês muda.' },
                        { nome: 'Recalcular período', texto: 'Reconstrói os resumos a partir dos lançamentos de verdade. Use se algum mês aparecer sem dados ou se você desconfiar de algum total.' },
                        { nome: 'Exportar Excel', texto: 'Baixa uma planilha com três abas: consolidado mês a mês, por barbeiro e por categoria. Os valores vão como número, então dá para somar e filtrar normalmente.' }
                    ]
                },
                {
                    titulo: 'Receitas',
                    icone: 'mdi-cash-plus',
                    cor: 'teal darken-2',
                    resumo: 'Tudo que entrou no mês. A tabela é editável direto na linha, como uma planilha.',
                    itens: [
                        { nome: 'Importar da agenda', texto: 'Traz os atendimentos agendados no mês com barbeiro, serviço e valor já preenchidos. O vínculo é feito por atendimento, então rodar de novo não duplica nada — só traz o que ainda falta.' },
                        { nome: 'Lançamento livre', texto: 'Para o que não passou pela agenda: venda de produto, pacote, gorjeta. Só valor e data são obrigatórios; a descrição é livre. O barbeiro é opcional — sem ele, não há comissão.' },
                        { nome: 'Recalcular pela agenda', texto: 'Traz de volta o valor que está na agenda, para os lançamentos importados. Preserva a forma de pagamento, o desconto e o status que você já preencheu.' },
                        { nome: 'Salvar', texto: 'A edição na tabela não grava sozinha. O botão mostra quantas linhas foram alteradas e salva todas de uma vez.' },
                        { nome: 'Categoria', texto: 'É ela que define qual percentual de comissão vale. Ao salvar, a categoria escolhida fica memorizada para aquele serviço, então a próxima importação já vem classificada.' },
                        { nome: 'Status', texto: 'Recebido entra no resultado; Pendente é o que ainda não foi pago; Cancelado fica registrado mas não conta.' }
                    ],
                    atencao: 'Alterações feitas na tabela só valem depois de clicar em Salvar. Importar ou recalcular recarrega a tela e descarta o que não foi salvo.'
                },
                {
                    titulo: 'Despesas',
                    icone: 'mdi-cash-minus',
                    cor: 'teal darken-2',
                    resumo: 'Tudo que saiu do caixa. Classificar bem é o que permite enxergar o custo real.',
                    itens: [
                        { nome: 'Fixa ou variável', texto: 'Fixa repete mesmo com pouco movimento: aluguel, internet, sistema, contador. Variável acompanha o movimento: material descartável, consumo por atendimento.' },
                        { nome: 'Direta ou indireta', texto: 'Direta está ligada ao serviço prestado. Indireta mantém a operação de pé como um todo.' },
                        { nome: 'Pago ou em aberto', texto: 'Só o que está Pago entra no lucro do mês. O que está em aberto aparece como aviso no painel, para não ser esquecido.' },
                        { nome: 'Vencimento', texto: 'Campo opcional, útil para acompanhar o que ainda vai vencer.' }
                    ]
                },
                {
                    titulo: 'Equipe e comissões',
                    icone: 'mdi-account-group',
                    cor: 'teal darken-2',
                    resumo: 'Quanto cada barbeiro recebe por tipo de serviço e qual a meta dele.',
                    itens: [
                        { nome: 'Percentuais', texto: 'Informe em porcentagem: 40 significa 40%. Há um campo para corte, barba, corte + barba e outros — qualquer categoria fora das três primeiras usa o percentual de "outros".' },
                        { nome: 'Meta mensal', texto: 'Valor de faturamento esperado do barbeiro. O painel mostra quanto da meta foi atingido no mês.' },
                        { nome: 'Barbeiro removido do cadastro', texto: 'O percentual dele é preservado e marcado como inativo, para o histórico não ser recalculado com valores diferentes dos combinados.' }
                    ],
                    atencao: 'Mudar um percentual recalcula também os meses já fechados, como acontecia na planilha. Se precisar preservar um fechamento antigo, exporte-o em Excel antes de alterar.'
                },
                {
                    titulo: 'Configurações',
                    icone: 'mdi-cog',
                    cor: 'teal darken-2',
                    resumo: 'Os parâmetros que alimentam os cálculos e as listas de seleção.',
                    itens: [
                        { nome: 'Taxas de débito e crédito', texto: 'Percentual cobrado pela maquininha. É descontado do valor já líquido de desconto, e só em pagamentos nessas duas formas.' },
                        { nome: 'Taxa manual de crédito', texto: 'Na aba Despesas, use a categoria Taxa de crédito para informar o total cobrado pela maquininha no mês, sem classificar cada receita. Para não duplicar, deixe a taxa automática de crédito em zero no mesmo período.' },
                        { nome: 'Meta de margem', texto: 'Referência mostrada ao lado da margem no painel. Não altera nenhum cálculo.' },
                        { nome: 'Listas', texto: 'Formas de pagamento, categorias de serviço e categorias de despesa. São o que aparece nas caixas de seleção dos lançamentos. Digite e pressione Enter para incluir.' }
                    ],
                    atencao: 'Alterar uma taxa recalcula os lançamentos já registrados, inclusive de meses anteriores.'
                }
            ],

            duvidas: [
                {
                    pergunta: 'Preciso lançar tudo à mão todo mês?',
                    resposta: 'Não. Os atendimentos vêm da agenda por importação. À mão entram só as despesas e o que não passou pela agenda.'
                },
                {
                    pergunta: 'Importei duas vezes. Duplicou?',
                    resposta: 'Não. Cada lançamento fica vinculado ao atendimento de origem, e a importação ignora o que já foi trazido.'
                },
                {
                    pergunta: 'Mudei o preço de um serviço. E os lançamentos antigos?',
                    resposta: 'Continuam com o valor que tinham quando foram lançados. Se quiser trazê-los para o preço atual da agenda, use "Recalcular pela agenda" na aba Receitas.'
                },
                {
                    pergunta: 'O lucro apareceu negativo. Está errado?',
                    resposta: 'Não necessariamente. Se as despesas pagas do mês superarem a receita após comissões, o resultado é negativo mesmo. Vale conferir se alguma despesa de outro mês foi lançada com a data errada.'
                },
                {
                    pergunta: 'Um mês aparece vazio no consolidado.',
                    resposta: 'É porque ele ainda não tem resumo salvo. Clique em "Recalcular período" que os resumos são gerados a partir dos lançamentos.'
                },
                {
                    pergunta: 'A soma por barbeiro não bate com o faturamento.',
                    resposta: 'Confira a linha "Sem barbeiro (lançamentos livres)". Ela reúne o que foi lançado sem vincular a um profissional e fecha a diferença.'
                }
            ]
        }
    }
}
</script>

<style scoped>
.secao-titulo {
    font-size: 18px;
    font-weight: 800;
    color: #14312d;
}

.bloco-titulo {
    font-size: 15px;
    font-weight: 700;
    color: #14312d;
}

.passo {
    display: flex;
    gap: 14px;
    align-items: flex-start;
}

.passo + .passo {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(0, 105, 92, 0.1);
}

.passo__numero {
    width: 30px;
    height: 30px;
    border-radius: 999px;
    background: #00695c;
    color: #ffffff;
    font-size: 13px;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 30px;
}

.passo__titulo {
    font-size: 15px;
    font-weight: 700;
    color: #14312d;
}

.passo__texto {
    font-size: 13px;
    color: #55657c;
    margin-top: 2px;
}

.cascata {
    border-radius: 14px;
    overflow: hidden;
    border: 1px solid #d5e5e2;
}

.cascata__linha {
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 9px 14px;
    font-size: 14px;
    color: #14312d;
    background: #ffffff;
}

.cascata__linha + .cascata__linha {
    border-top: 1px solid #eef5f4;
}

.cascata__linha--base {
    background: #f7fbfa;
    font-weight: 700;
}

.cascata__linha--parcial {
    background: #f7fbfa;
    font-weight: 700;
}

.cascata__linha--total {
    background: #e8f5e9;
    font-weight: 800;
}

.cascata__operador {
    width: 16px;
    flex: 0 0 16px;
    color: #6b8480;
    font-weight: 700;
    text-align: center;
}

.cascata__rotulo {
    flex: 0 0 auto;
}

.cascata__nota {
    font-size: 12px;
    color: #6b8480;
}

.item-ajuda + .item-ajuda {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #eceff1;
}

.item-ajuda__nome {
    font-size: 14px;
    font-weight: 700;
    color: #14312d;
}

.item-ajuda__texto {
    font-size: 13px;
    color: #55657c;
    margin-top: 2px;
}

.duvida + .duvida {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #eceff1;
}

.duvida__pergunta {
    font-size: 14px;
    font-weight: 700;
    color: #14312d;
}

.duvida__resposta {
    font-size: 13px;
    color: #55657c;
    margin-top: 2px;
}

@media (max-width: 600px) {
    .cascata__linha {
        flex-wrap: wrap;
        gap: 4px 10px;
    }
}
</style>
